<template>
  <div class="settings-video-container">
    <label>{{ $tr('vacancy.preferences.settings.videoLink') }}</label>

    <div class="row-block">
      <div class="input-group">
        <input
          ref="input"
          v-validate="'url|verify_url'"
          type="text"
          name="videoLinkField"
          :value="videoLinkCopy"
          :class="{
            error: errors.has('videoLinkField'),
            valid: fields.videoLinkField ? fields.videoLinkField.valid : null
          }"
          @input="videoLinkChange"
        />
      </div>
    </div>

    <label v-show="errors.has('videoLinkField')" class="helper">
      {{ errors.first('videoLinkField') }}
    </label>

    <div v-show="!errors.has('videoLinkField') && videoLinkCopy" class="video-container">
      <iframe
        width="100%"
        height="100%"
        :src="videoLinkCopy"
        frameborder="0"
        allow="autoplay; encrypted-media"
        webkitallowfullscreen
        mozallowfullscreen
        allowfullscreen
      ></iframe>
    </div>
  </div>
</template>

<script>
import Validator from 'validator';

const isVimeo = urlString => urlString.indexOf('vimeo') !== -1;
const isYoutube = urlString => urlString.indexOf('youtube') !== -1 || urlString.indexOf('youtu.be') !== -1;

const acceptHostnames = [
  'youtube.com',
  'www.youtube.com',
  'youtu.be',
  'www.youtu.be',
  'vimeo.com',
  'www.vimeo.com',
  'player.vimeo.com',
  'www.player.vimeo.com'
];

const dictionary = {
  en: {
    messages: { verify_url: n => 'Field ' + n + ' must be a valid YouTube or Vimeo link.' },
    attributes: { videoLinkField: 'video link' }
  },
  ru: {
    messages: { verify_url: n => 'Поле ' + n + ' должно быть валидным url-адресом на видео YouTube или Vimeo.' },
    attributes: { videoLinkField: 'ссылка на видео' }
  }
};

export default {
  name: 'SettingsVideoContainer',

  props: {
    vacancy: Object,
    displayVideo: {
      type: Boolean,
      default: false
    },

    removeVideoLink: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      videoLinkCopy: '',
      editableVideoLink: '',
      editMode: false
    };
  },

  computed: {
    modelVideoLink() {
      return this.vacancy ? this.vacancy.video_link : null
    },

    errorsSave() {
      const fields = Object.keys(this.fields).map(e => this.fields[e]);

      return fields.every(el => el.valid);
    }
  },

  watch: {
    errorsSave(val) {
      this.$emit('errSave', val);
    },

    removeVideoLink(val) {
      if (val) {
        this.videoLinkCopy = '';
        this.$emit('setVideoLink', this.videoLinkCopy);
      }
    }
  },

  mounted() {
    this.videoLinkCopy = this.modelVideoLink;

    this.$parent.$on('saveOnParent', () => {
      if (this.editMode) {
        this.saveVacancyVideo();
      }
    });
  },

  created() {
    this.$validator.extend('verify_url', {
      getMessage: field => 'Field' + field + ' must be a valid YouTube or Vimeo link.',
      validate: value => this.validUrl(value)
    });

    this.$validator.localize(dictionary);
  },

  destroyed() {
    this.$parent.$off('saveOnParent');
  },

  methods: {
    validUrl(urlString) {
      if (!Validator.isURL(urlString)) {
        return false;
      }

      let urlElem = document.createElement('a');
      urlElem.href = urlString;

      return acceptHostnames.includes(urlElem.hostname);
    },

    parseVimeo(urlElem) {
      const lastPathname = urlElem => urlElem.pathname.split('/').pop();

      const id = lastPathname(urlElem);

      return id ? `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0` : null;
    },

    parseYoutube(urlElem) {
      const isYoutubeWatchLink = urlElem =>
        urlElem.pathname.indexOf('watch') !== -1 && urlElem.search.indexOf('v') !== -1;
      const lastPathname = urlElem => urlElem.pathname.split('/').pop();

      // Case: https://www.youtube.com/watch?v=86zlYfBvkiM
      if (isYoutubeWatchLink(urlElem)) {
        // Cut "?" and separate by "&"
        const params = urlElem.search.slice(1).split('&');
        const v = params.find(p => p[0] === 'v');

        // Cut "v="
        const id = v.slice(2);

        return id ? `https://www.youtube.com/embed/${id}?rel=0&amp;showinfo=0&amp;playsinline=1` : null;
      } else {
        const id = lastPathname(urlElem);

        return id ? `https://www.youtube.com/embed/${id}?rel=0&amp;showinfo=0&amp;playsinline=1` : null;
      }
    },

    parseUrl(urlString) {
      // test ref for iframe
      const regexp = /src="([^"]*)"/g;
      const res = regexp.exec(urlString);

      if (res && res.length) {
        urlString = res.pop();
      }

      if (!this.validUrl(urlString)) {
        return null;
      }

      let urlElem = document.createElement('a');
      urlElem.href = urlString;

      if (isVimeo(urlString)) {
        return this.parseVimeo(urlElem);
      } else if (isYoutube(urlString)) {
        return this.parseYoutube(urlElem);
      }
    },

    toggleEditMode() {
      this.editMode = !this.editMode;
      this.videoLinkCopy = this.modelVideoLink;

      if (this.editMode) {
        setTimeout(() => {
          this.$refs.input.focus();
        }, 100);
      }
    },

    saveVacancyVideo() {
      this.$store.commit('vacancy/update', {
        model: this.vacancy,
        data: { video_link: this.videoLinkCopy }
      });
      this.$store.dispatch('vacancy/save', this.vacancy);
      this.toggleEditMode();
    },

    async videoLinkChange(e) {
      const val = e.target.value;
      const iframe = this.parseUrl(val);

      // Assign empty string to case
      // when copy-paste same value
      // and visible value not changed
      this.videoLinkCopy = '';
      if (iframe) {
        this.videoLinkCopy = iframe;
        this.$emit('setVideoLink', this.videoLinkCopy);
        await this.$nextTick();
        this.$validator.validateAll();
      } else {
        this.videoLinkCopy = val;
        this.$emit('setVideoLink', this.videoLinkCopy);
      }
      if (this.displayVideo) {
        this.$emit('setChange', true);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.settings-video-container {
  width: 100%;
}

label {
  @include mixin-text-header;
  display: block;
  margin-bottom: 10px;
}

.row-block {
  display: flex;
}

input {
  height: 40px;
  width: 100%;
  padding-top: 8px;
  padding-bottom: 8px;
}

button {
  border: none;
  &:hover {
    cursor: pointer;
  }
}

.input-group {
  display: flex;
  position: relative;
  width: 100%;

  input {
    border-radius: 5px;
    // border-right-width: 0;
  }

  .accept {
    height: 40px;
    width: 40px;
    min-width: 40px;
    background-color: $green;
    background-image: url('/static/images/elements/trigger_on.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 15px;
    border-radius: 0 5px 5px 0;

    &:disabled {
      background-color: $btn-red;
    }
  }
}

.cancel {
  margin-left: 10px;
  color: $green;
  font-weight: 500;
  font-size: 16px;
  background: transparent;
}

label.helper {
  display: block;
  margin: 0;
  color: #a6a6a6;
  line-height: 20px;
  font-size: 14px;
  color: $error-color;
  padding-top: 5px;
}

.video-container {
  padding-top: 56.25%;
  position: relative;
  margin-top: 10px;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>
